// Fetch a Mapbox-hosted style and adapt it for MapLibre.
//
// Mapbox styles use a few expression types MapLibre never adopted —
// notably `pitch` and `distance-from-center` filter expressions, plus a
// stray `name` property at the style root. MapLibre 4 *and* 5 both reject
// them; affected layers get dropped, leaving an empty canvas.
//
// We can't avoid the unsupported expressions in the upstream style, so
// we fetch the style JSON ourselves, drop layers whose filters reference
// any unsupported expression, and remove unknown root keys, before handing
// the cleaned spec to MapLibre.

import type maplibregl from "maplibre-gl";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOXGL_TOKEN as string;

const UNSUPPORTED_EXPRESSIONS = new Set([
  "pitch",
  "distance-from-center",
  "measure-light",
  "config",
]);

function filterUsesUnsupported(expr: unknown): boolean {
  if (!Array.isArray(expr)) return false;
  if (typeof expr[0] === "string" && UNSUPPORTED_EXPRESSIONS.has(expr[0])) {
    return true;
  }
  return expr.some(filterUsesUnsupported);
}

interface RawStyle {
  layers?: Array<{ id?: string; filter?: unknown; [k: string]: unknown }>;
  // Mapbox-specific keys MapLibre flags as "unknown property".
  name?: unknown;
  metadata?: unknown;
  imports?: unknown;
  schema?: unknown;
  [k: string]: unknown;
}

const cache = new Map<string, Promise<maplibregl.StyleSpecification>>();

export function loadMapboxStyle(
  styleId: string
): Promise<maplibregl.StyleSpecification> {
  const cached = cache.get(styleId);
  if (cached) return cached;
  const promise = fetchAndClean(styleId);
  cache.set(styleId, promise);
  promise.catch(() => cache.delete(styleId));
  return promise;
}

async function fetchAndClean(
  styleId: string
): Promise<maplibregl.StyleSpecification> {
  const url = `https://api.mapbox.com/styles/v1/${styleId}?access_token=${MAPBOX_TOKEN}`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`mapbox style fetch failed: ${res.status} ${res.statusText}`);
  }
  const raw = (await res.json()) as RawStyle;

  const cleaned: RawStyle = { ...raw };
  delete cleaned.name;
  delete cleaned.imports;
  delete cleaned.schema;

  if (Array.isArray(raw.layers)) {
    cleaned.layers = raw.layers.filter(
      (layer) => !filterUsesUnsupported(layer.filter)
    );
  }

  return cleaned as unknown as maplibregl.StyleSpecification;
}

// Tiny stand-in style — handed to `new maplibregl.Map(...)` while the real
// style fetch is in flight. Flat dark canvas with no sources/layers, so the
// map mounts without flashing the default street basemap.
export const PLACEHOLDER_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {},
  layers: [
    {
      id: "background",
      type: "background",
      paint: { "background-color": "#0f172a" },
    },
  ],
};
